/* HARPAH Product Speed: Kiwi Size Guide on-demand loader.
 * Keeps Kiwi installed/data intact, defers it until size-guide intent,
 * exposes only one HARPAH Size Guide trigger, and waits for Kiwi scripts
 * to finish initializing before programmatically opening the native modal.
 */
(function () {
  'use strict';

  var triggerSelector = '.h-kiwi-size-trigger';
  var nativeSelectors = [
    '.ks-chart-modal-link.sizing-chart-modal-link',
    '.ks-chart-modal-link',
    '.sizing-chart-modal-link',
    '.kiwi-sizing-modal-link',
    '.kiwiSizingChart',
    '.ks-sizing-modal-link',
    '[data-kiwi-sizing-chart]',
    '[data-ks-chart-modal]'
  ];

  var loadPromise = null;
  var materialized = false;
  var preconnected = false;

  function delay(ms) {
    return new Promise(function (resolve) { window.setTimeout(resolve, ms); });
  }

  function addHint(rel, href) {
    if (!href || document.head.querySelector('link[rel="' + rel + '"][href="' + href + '"]')) return;
    var link = document.createElement('link');
    link.rel = rel;
    link.href = href;
    if (rel === 'preconnect') link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  }

  function warmKiwiConnection() {
    if (preconnected) return;
    preconnected = true;
    addHint('dns-prefetch', 'https://app.kiwisizing.com');
    addHint('preconnect', 'https://app.kiwisizing.com');
  }

  function findNativeTrigger(sourceButton) {
    var preferred = sourceButton && sourceButton.getAttribute('data-kiwi-trigger');
    if (preferred) {
      try {
        var preferredEl = document.querySelector(preferred);
        if (preferredEl && preferredEl !== sourceButton) return preferredEl;
      } catch (e) {}
    }

    for (var i = 0; i < nativeSelectors.length; i++) {
      var el = document.querySelector(nativeSelectors[i]);
      if (el && el !== sourceButton && !el.matches(triggerSelector)) return el;
    }
    return null;
  }

  /* Re-create scripts from an inert <template> and resolve only after external
   * scripts have loaded. This fixes desktop where the native Kiwi link could
   * exist before Kiwi had attached its modal click handler. */
  function activateScripts(root) {
    var scripts = Array.prototype.slice.call(root.querySelectorAll('script'));
    var waits = [];

    scripts.forEach(function (oldScript) {
      var script = document.createElement('script');
      Array.prototype.slice.call(oldScript.attributes).forEach(function (attr) {
        script.setAttribute(attr.name, attr.value);
      });

      if (oldScript.src || oldScript.getAttribute('src')) {
        waits.push(new Promise(function (resolve) {
          script.addEventListener('load', resolve, { once: true });
          script.addEventListener('error', resolve, { once: true });
          /* Never hold the Size Guide forever if a vendor load event is missed. */
          window.setTimeout(resolve, 5000);
        }));
      } else if (oldScript.textContent) {
        script.textContent = oldScript.textContent;
      }

      oldScript.parentNode.replaceChild(script, oldScript);
    });

    return Promise.all(waits);
  }

  function hideNativeTrigger(trigger) {
    if (!trigger || trigger.matches(triggerSelector)) return;
    trigger.setAttribute('data-h-kiwi-native-trigger', '');
    trigger.setAttribute('aria-hidden', 'true');
    trigger.setAttribute('tabindex', '-1');
    trigger.style.position = 'absolute';
    trigger.style.width = '1px';
    trigger.style.height = '1px';
    trigger.style.padding = '0';
    trigger.style.margin = '-1px';
    trigger.style.overflow = 'hidden';
    trigger.style.clip = 'rect(0 0 0 0)';
    trigger.style.clipPath = 'inset(50%)';
    trigger.style.whiteSpace = 'nowrap';
    trigger.style.opacity = '0';
    trigger.style.pointerEvents = 'none';
  }

  function hideAllNativeTriggers() {
    nativeSelectors.forEach(function (selector) {
      try {
        document.querySelectorAll(selector).forEach(function (el) {
          if (!el.matches(triggerSelector)) hideNativeTrigger(el);
        });
      } catch (e) {}
    });
  }

  function materializeKiwi() {
    if (loadPromise) return loadPromise;
    if (materialized) return Promise.resolve();

    loadPromise = new Promise(function (resolve, reject) {
      var template = document.querySelector('template[data-h-kiwi-app-template]');
      var host = document.querySelector('[data-h-kiwi-lazy-host]');

      if (!template || !host) {
        reject(new Error('Kiwi lazy template not found'));
        return;
      }

      warmKiwiConnection();

      host.style.position = 'absolute';
      host.style.width = '1px';
      host.style.height = '1px';
      host.style.overflow = 'hidden';
      host.style.clip = 'rect(0 0 0 0)';
      host.style.clipPath = 'inset(50%)';
      host.style.whiteSpace = 'nowrap';

      var fragment = template.content.cloneNode(true);
      var scriptsReady = activateScripts(fragment);
      host.appendChild(fragment);
      materialized = true;
      document.documentElement.classList.add('h-kiwi-materialized');

      /* Kiwi can inject its native link outside the hidden app host. Keep it
       * usable programmatically but never show a second Size Guide to shoppers. */
      hideAllNativeTriggers();

      Promise.resolve(scriptsReady)
        .then(function () { return delay(300); })
        .then(function () {
          hideAllNativeTriggers();
          resolve();
        });
    });

    return loadPromise;
  }

  function waitForNativeTrigger(sourceButton, timeoutMs) {
    return new Promise(function (resolve, reject) {
      var immediate = findNativeTrigger(sourceButton);
      if (immediate) {
        hideNativeTrigger(immediate);
        resolve(immediate);
        return;
      }

      var settled = false;
      var observer = new MutationObserver(function () {
        hideAllNativeTriggers();
        var trigger = findNativeTrigger(sourceButton);
        if (trigger && !settled) {
          settled = true;
          observer.disconnect();
          hideNativeTrigger(trigger);
          resolve(trigger);
        }
      });

      observer.observe(document.documentElement, { childList: true, subtree: true });

      window.setTimeout(function () {
        if (settled) return;
        var trigger = findNativeTrigger(sourceButton);
        observer.disconnect();
        if (trigger) {
          hideNativeTrigger(trigger);
          resolve(trigger);
        } else {
          reject(new Error('Kiwi size-chart trigger did not initialize'));
        }
      }, timeoutMs || 8000);
    });
  }

  function setBusy(button, busy) {
    if (!button) return;
    if (busy) {
      button.setAttribute('aria-busy', 'true');
    } else {
      button.removeAttribute('aria-busy');
    }
  }

  function openKiwi(button) {
    setBusy(button, true);
    warmKiwiConnection();

    /* Always go through materializeKiwi(), even if native markup exists.
     * The promise waits for Kiwi's external scripts and a short init settle,
     * preventing the desktop no-op caused by clicking too early. */
    materializeKiwi()
      .then(function () { return waitForNativeTrigger(button, 8000); })
      .then(function (nativeTrigger) { return delay(120).then(function () { return nativeTrigger; }); })
      .then(function (nativeTrigger) {
        setBusy(button, false);
        nativeTrigger.click();
      })
      .catch(function (error) {
        setBusy(button, false);
        console.warn('[HARPAH] Kiwi size guide could not be opened lazily.', error);
        document.dispatchEvent(new CustomEvent('harpah:kiwi-load-error', { detail: { error: String(error) } }));
      });
  }

  function onIntent(event) {
    var button = event.target && event.target.closest ? event.target.closest(triggerSelector) : null;
    if (!button) return;
    warmKiwiConnection();

    /* On touch/mobile, start initialization on the earliest trusted intent.
     * Desktop remains on-demand so Kiwi is not pushed into the initial PDP path. */
    if (window.matchMedia && window.matchMedia('(max-width: 768px)').matches) {
      materializeKiwi().catch(function () {});
    }
  }

  function onClick(event) {
    var button = event.target && event.target.closest ? event.target.closest(triggerSelector) : null;
    if (!button) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    openKiwi(button);
  }

  document.addEventListener('pointerover', onIntent, true);
  document.addEventListener('focusin', onIntent, true);
  document.addEventListener('touchstart', onIntent, { capture: true, passive: true });
  document.addEventListener('click', onClick, true);

  function observeSizeGuide() {
    if (!('IntersectionObserver' in window)) return;
    var buttons = document.querySelectorAll(triggerSelector);
    if (!buttons.length) return;

    var isMobile = window.matchMedia && window.matchMedia('(max-width: 768px)').matches;
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;

        warmKiwiConnection();

        if (isMobile) {
          materializeKiwi()
            .then(function () { return waitForNativeTrigger(entry.target, 8000); })
            .then(function () { hideAllNativeTriggers(); })
            .catch(function () {});
        }

        observer.unobserve(entry.target);
      });
    }, { rootMargin: isMobile ? '220px 0px' : '500px 0px' });

    buttons.forEach(function (button) { observer.observe(button); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeSizeGuide, { once: true });
  } else {
    observeSizeGuide();
  }
})();
